"use client";

import { getSessionId } from "./session-id";
import { templateRoute } from "./route-template";

let started = false;

type LogSeverity = "INFO" | "WARN" | "ERROR";
let emitLog:
  | ((severity: LogSeverity, body: string, attributes?: Record<string, unknown>) => void)
  | null = null;

// --- Metrics plumbing -------------------------------------------------------
// The OTel metric instruments are created asynchronously inside `initTelemetry`
// (dynamic imports). Recording entry points (`recordWebVital`, soft-nav, the
// fetch wrapper) may fire before that resolves — especially FCP/TTFB, which the
// browser reports during initial load — so we buffer until the meter is ready.
// When telemetry is disabled (no endpoint, e.g. production) everything is a
// cheap no-op and nothing is buffered.

/** Minimal structural view of an OTel Histogram, so no static SDK import leaks. */
type Recorder = { record: (value: number, attributes?: Record<string, string>) => void };
let vitalTiming: Recorder | null = null;
let vitalCls: Recorder | null = null;
let softNav: Recorder | null = null;
let apiDuration: Recorder | null = null;

let telemetryEnabled = false;
let meterReady = false;
const metricQueue: Array<() => void> = [];

function enqueueMetric(fn: () => void): void {
  if (!telemetryEnabled) return;
  if (meterReady) {
    fn();
  } else if (metricQueue.length < 1000) {
    metricQueue.push(fn);
  }
}

/** The subset of the web-vitals `Metric` that `useReportWebVitals` passes us. */
type WebVitalMetric = { name: string; value: number; rating?: string; navigationType?: string };

/**
 * Record a Core Web Vital. Fed by `next/web-vitals`' `useReportWebVitals`
 * (see `WebVitalsReporter`). `FID` is dropped — it is deprecated and superseded
 * by `INP`, which the same hook already reports.
 */
export function recordWebVital(metric: WebVitalMetric): void {
  if (metric.name === "FID") return;
  enqueueMetric(() => {
    const attributes = {
      vital: metric.name,
      rating: metric.rating ?? "unknown",
      navigation_type: metric.navigationType ?? "unknown",
    };
    if (metric.name === "CLS") vitalCls?.record(metric.value, attributes);
    else vitalTiming?.record(metric.value, attributes);
  });
}

let pendingNavStart: number | null = null;

/**
 * Mark the start of a client-side (soft) navigation. Called by the blocking
 * router (`useBlockableRouter`) the moment a confirmed navigation begins, so it
 * covers every `<Link>` click and programmatic `router.push`/`replace`.
 */
export function markSoftNavStart(): void {
  if (typeof performance === "undefined") return;
  pendingNavStart = performance.now();
}

/**
 * Record a soft navigation's duration once its destination `pathname` has
 * committed (called from `SoftNavigationTracker` after paint). No-op unless a
 * start was marked, so initial loads and back/forward are not mis-attributed.
 */
export function recordSoftNavEnd(pathname: string): void {
  const start = pendingNavStart;
  pendingNavStart = null;
  if (start == null || typeof performance === "undefined") return;
  const value = performance.now() - start;
  enqueueMetric(() => softNav?.record(value, { route: templateRoute(pathname) }));
}

/**
 * Initialise browser OpenTelemetry (traces + logs) for the given OTLP/HTTP
 * `endpoint`; a no-op when `endpoint` is falsy, so normal dev and Playwright e2e
 * (and any environment without an endpoint, e.g. production) stay infra-free.
 *
 * The endpoint is supplied at runtime (a server env var threaded through
 * `Providers`), not a build-time `NEXT_PUBLIC_` var, so the same image can have
 * telemetry on in staging and off in production.
 *
 * - Traces: a span per fetch via `instrumentation-fetch`, which also injects the
 *   W3C `traceparent` header so the backend span becomes a child of the browser
 *   span — one `trace_id` from click to SQL query. Requests are same-origin
 *   (`/api/*` proxied), so trace-header propagation needs no CORS allow-list.
 *   The endpoint itself should also be same-origin (e.g. a `/otlp` proxy) so the
 *   exporter POSTs avoid CORS.
 * - Logs: browser log/error lines carrying `session.id` → Loki.
 * - Metrics: Core Web Vitals, soft-navigation duration, and client-observed
 *   `/api` request latency (histograms → Prometheus). Vitals come from
 *   `useReportWebVitals`, soft-nav from the blocking router, API latency from a
 *   fetch wrapper installed here.
 *
 * The OTel SDK is imported dynamically so it never lands in the SSR or initial
 * client bundle.
 */
export async function initTelemetry(endpoint?: string, userId?: string): Promise<void> {
  if (started || typeof window === "undefined") return;
  if (!endpoint) return;
  started = true;
  // From here telemetry is on: recording entry points may buffer until the
  // meter is ready (set below). Kept off above so a no-endpoint build never
  // accumulates buffered metrics.
  telemetryEnabled = true;

  const base = endpoint.replace(/\/$/, "");

  const [
    { WebTracerProvider, BatchSpanProcessor },
    { OTLPTraceExporter },
    { FetchInstrumentation },
    { registerInstrumentations },
    { ZoneContextManager },
    { resourceFromAttributes },
    { ATTR_SERVICE_NAME },
    { LoggerProvider, BatchLogRecordProcessor },
    { OTLPLogExporter },
    logsApi,
    { MeterProvider, PeriodicExportingMetricReader, AggregationType },
    { OTLPMetricExporter },
  ] = await Promise.all([
    import("@opentelemetry/sdk-trace-web"),
    import("@opentelemetry/exporter-trace-otlp-http"),
    import("@opentelemetry/instrumentation-fetch"),
    import("@opentelemetry/instrumentation"),
    import("@opentelemetry/context-zone"),
    import("@opentelemetry/resources"),
    import("@opentelemetry/semantic-conventions"),
    import("@opentelemetry/sdk-logs"),
    import("@opentelemetry/exporter-logs-otlp-http"),
    import("@opentelemetry/api-logs"),
    import("@opentelemetry/sdk-metrics"),
    import("@opentelemetry/exporter-metrics-otlp-http"),
  ]);

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: "ha-frontend",
    "session.id": getSessionId() ?? "unknown",
    ...(userId ? { "user.id": userId } : {}),
  });

  // Traces.
  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: `${base}/v1/traces` })),
    ],
  });
  tracerProvider.register({ contextManager: new ZoneContextManager() });
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Same-origin /api/* requests; propagate trace context to all of them.
        propagateTraceHeaderCorsUrls: [/.*/],
      }),
    ],
  });

  // Logs.
  const loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor(new OTLPLogExporter({ url: `${base}/v1/logs` })),
    ],
  });
  logsApi.logs.setGlobalLoggerProvider(loggerProvider);
  const logger = logsApi.logs.getLogger("ha-frontend");
  emitLog = (severity, body, attributes) => {
    logger.emit({
      severityText: severity,
      body,
      attributes: { "session.id": getSessionId() ?? "unknown", ...attributes },
    });
  };

  // Metrics (histograms → Prometheus). Values are recorded through the module
  // entry points above — this block just builds the instruments and, once
  // ready, drains anything buffered while the dynamic imports were in flight.
  // The periodic reader flushes every 15s; a visibilitychange→hidden flush
  // catches final values (e.g. CLS/INP) as the page is backgrounded or closed.
  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: new OTLPMetricExporter({ url: `${base}/v1/metrics` }),
        exportIntervalMillis: 15000,
      }),
    ],
    // CLS values cluster in [0, 1]; override the default (ms-scaled) buckets so
    // its histogram has useful resolution.
    views: [
      {
        instrumentName: "ha_frontend_web_vital_cls",
        aggregation: {
          type: AggregationType.EXPLICIT_BUCKET_HISTOGRAM,
          options: { boundaries: [0, 0.05, 0.1, 0.15, 0.25, 0.5, 1] },
        },
      },
    ],
  });
  const meter = meterProvider.getMeter("ha-frontend");
  vitalTiming = meter.createHistogram("ha_frontend_web_vital_timing", {
    description: "Core Web Vitals timings (LCP, FCP, TTFB, INP).",
    unit: "ms",
  });
  vitalCls = meter.createHistogram("ha_frontend_web_vital_cls", {
    description: "Cumulative Layout Shift (unitless).",
  });
  softNav = meter.createHistogram("ha_frontend_soft_navigation_duration", {
    description: "Client-side (soft) navigation duration via the app router.",
    unit: "ms",
  });
  apiDuration = meter.createHistogram("ha_frontend_api_request_duration", {
    description: "Client-observed /api request latency (includes network/proxy).",
    unit: "ms",
  });
  meterReady = true;
  for (const fn of metricQueue.splice(0)) fn();

  // Client-observed API latency: wrap the (already fetch-instrumented) global
  // fetch to time same-origin /api/* calls by templated route, method and
  // status class. Skips the OTLP exporter's own POSTs to /otlp to avoid a
  // feedback loop. FetchInstrumentation still owns the span + traceparent;
  // this only adds a metric.
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [input, init] = args;
    const url =
      typeof input === "string" ? input : input instanceof Request ? input.url : String(input);
    if (!url.includes("/api/") || url.includes("/otlp/")) return originalFetch(...args);
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const route = templateRoute(url);
    const start = performance.now();
    try {
      const res = await originalFetch(...args);
      apiDuration?.record(performance.now() - start, {
        route,
        method,
        status_class: `${Math.floor(res.status / 100)}xx`,
      });
      return res;
    } catch (error) {
      apiDuration?.record(performance.now() - start, { route, method, status_class: "error" });
      throw error;
    }
  };

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void meterProvider.forceFlush();
  });

  // Capture uncaught browser errors into the correlated log stream.
  window.addEventListener("error", (event) => {
    emitLog?.("ERROR", event.message, { "error_stack": event.error?.stack });
  });
  window.addEventListener("unhandledrejection", (event) => {
    emitLog?.("ERROR", `Unhandled rejection: ${String(event.reason)}`);
  });

  // Mirror `console.*` calls into the correlated log stream, while keeping the
  // original console output intact. Each patched method calls through to the
  // native implementation first, then forwards a stringified line to OTel.
  const patch = (method: "log" | "info" | "warn" | "error", severity: LogSeverity) => {
    const original = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      original(...args);
      const extra_args: Record<string, string> = { "error_cause": "console." + method };
      if (args[0] instanceof Error) {
        const error = args[0] as Error;
        extra_args["error_name"] = error.name;
        if (error.stack) extra_args["error_stack"] = error.stack;
      }
      emitLog?.(severity, args.map(formatArg).join(";; "), extra_args);
    };
  };
  patch("log", "INFO");
  patch("info", "INFO");
  patch("warn", "WARN");
  patch("error", "ERROR");
}

/** Render a single `console.*` argument to a log-friendly string. */
function formatArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error)
    return `${arg.name}: ${arg.message}`;
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    return String(arg);
  }
}

/**
 * Emit a browser log line correlated with the current session (and active trace,
 * if any). Falls back to the console when telemetry is disabled.
 */
export function log(severity: LogSeverity, body: string, attributes?: Record<string, unknown>): void {
  if (emitLog) {
    emitLog(severity, body, attributes);
  } else if (severity === "ERROR") {
    console.error(body, attributes);
  }
}
